import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import MoodOrb from '../components/MoodOrb';
import MusicPlayer from '../components/MusicPlayer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import { 
    MapPin, Music, Sparkles, Save, Loader2, 
    ChevronLeft, ChevronRight, Palette, Sticker, X, Mic, Square, Play, Pause, Trash2, PenLine
} from 'lucide-react';

const EMOJI_OPTIONS = ['😊', '😢', '😤', '😰', '😴', '🥳', '😌', '🤔', '😍', '😔', '🙂', '😎'];

const STICKER_OPTIONS = [
    { id: 'heart', emoji: '❤️', name: 'Heart' },
    { id: 'star', emoji: '⭐', name: 'Star' },
    { id: 'sun', emoji: '☀️', name: 'Sun' },
    { id: 'moon', emoji: '🌙', name: 'Moon' },
    { id: 'rainbow', emoji: '🌈', name: 'Rainbow' },
    { id: 'flower', emoji: '🌸', name: 'Flower' },
    { id: 'butterfly', emoji: '🦋', name: 'Butterfly' },
    { id: 'sparkle', emoji: '✨', name: 'Sparkle' },
    { id: 'cloud', emoji: '☁️', name: 'Cloud' },
    { id: 'leaf', emoji: '🍃', name: 'Leaf' },
    { id: 'fire', emoji: '🔥', name: 'Fire' },
    { id: 'water', emoji: '💧', name: 'Water' },
    { id: 'peace', emoji: '☮️', name: 'Peace' },
    { id: 'pray', emoji: '🙏', name: 'Grateful' },
    { id: 'muscle', emoji: '💪', name: 'Strength' },
    { id: 'brain', emoji: '🧠', name: 'Mindful' }
];

const NewEntryPage = () => {
    const navigate = useNavigate();
    const { api, user } = useAuth();
    
    // Get user's saved preferences for journal customization
    const userPrefs = user?.theme_preferences || {};
    
    const [step, setStep] = useState(1);
    const [entryType, setEntryType] = useState('text'); // 'text' or 'voice'
    const [moodRating, setMoodRating] = useState(5);
    const [emoji, setEmoji] = useState('😊');
    const [songOrGenre, setSongOrGenre] = useState('');
    const [content, setContent] = useState('');
    const [location, setLocation] = useState(null);
    const [locationName, setLocationName] = useState('');
    const [loadingLocation, setLoadingLocation] = useState(false);
    const [prompts, setPrompts] = useState([]);
    const [loadingPrompts, setLoadingPrompts] = useState(false);
    const [saving, setSaving] = useState(false);
    const [customColors, setCustomColors] = useState({
        background: userPrefs.journal_background || '',
        accent: userPrefs.accent_color || ''
    });
    const [textColor, setTextColor] = useState(userPrefs.text_color || '');
    const [fontFamily, setFontFamily] = useState(userPrefs.font_family || '');
    const [selectedStickers, setSelectedStickers] = useState([]);
    
    // Voice recording state
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [recordingDuration, setRecordingDuration] = useState(0);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRef = useRef(null);
    const timerRef = useRef(null);

    const toggleSticker = (stickerId) => {
        setSelectedStickers(prev => 
            prev.includes(stickerId) 
                ? prev.filter(s => s !== stickerId)
                : [...prev, stickerId]
        );
    };

    // Voice recording functions
    const [audioMimeType, setAudioMimeType] = useState('audio/webm');
    const [recordingSupported, setRecordingSupported] = useState(true);
    const [recordingError, setRecordingError] = useState(null);

    // Check for recording support on mount
    useEffect(() => {
        // Check if MediaRecorder is supported
        if (!window.MediaRecorder) {
            setRecordingSupported(false);
            setRecordingError('Voice recording is not supported on this browser.');
            return;
        }

        // Check if getUserMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setRecordingSupported(false);
            setRecordingError('Voice recording is not supported on this browser.');
            return;
        }

        // Detect the best supported audio format
        // iOS Safari supports mp4/aac, Chrome/Firefox support webm/opus
        const mimeTypes = [
            'audio/mp4',           // iOS Safari preferred
            'audio/aac',           // iOS Safari fallback
            'audio/webm;codecs=opus', // Chrome/Firefox preferred
            'audio/webm',          // Chrome/Firefox fallback
            'audio/ogg;codecs=opus',  // Firefox alternative
            ''                     // Default (let browser choose)
        ];

        let supportedType = '';
        for (const type of mimeTypes) {
            if (type === '' || MediaRecorder.isTypeSupported(type)) {
                supportedType = type;
                break;
            }
        }
        
        setAudioMimeType(supportedType);
        console.log('Selected audio format:', supportedType || 'browser default');
    }, []);

    const startRecording = async () => {
        if (!recordingSupported) {
            toast.error(recordingError || 'Voice recording is not supported on this device.');
            return;
        }

        try {
            // Request microphone permission with specific constraints for better mobile compatibility
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                } 
            });

            // Create MediaRecorder with the best supported format
            let mediaRecorder;
            try {
                const options = audioMimeType ? { mimeType: audioMimeType } : {};
                mediaRecorder = new MediaRecorder(stream, options);
            } catch (e) {
                // Fallback: create without specifying mimeType
                console.warn('Failed with mimeType, using default:', e);
                mediaRecorder = new MediaRecorder(stream);
            }

            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];
            setRecordingDuration(0);

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = () => {
                // Use the actual mimeType from the recorder
                const actualMimeType = mediaRecorder.mimeType || audioMimeType || 'audio/webm';
                const blob = new Blob(audioChunksRef.current, { type: actualMimeType });
                const url = URL.createObjectURL(blob);
                setAudioBlob(blob);
                setAudioUrl(url);
                stream.getTracks().forEach(track => track.stop());
                clearInterval(timerRef.current);
            };

            // Use timeslice to get data periodically (better for mobile)
            mediaRecorder.start(1000);
            setIsRecording(true);
            
            // Start timer
            timerRef.current = setInterval(() => {
                setRecordingDuration(prev => prev + 1);
            }, 1000);
            
            toast.info('Recording started... Speak now!');
        } catch (error) {
            console.error('Failed to start recording:', error);
            
            // Provide more specific error messages
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                toast.error('Microphone access denied. Please allow microphone access in your browser settings and reload the page.');
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                toast.error('No microphone found. Please connect a microphone and try again.');
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                toast.error('Microphone is in use by another app. Please close other apps using the microphone.');
            } else if (error.name === 'OverconstrainedError') {
                // Try again with simpler constraints
                try {
                    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    const mediaRecorder = new MediaRecorder(stream);
                    mediaRecorderRef.current = mediaRecorder;
                    audioChunksRef.current = [];
                    setRecordingDuration(0);

                    mediaRecorder.ondataavailable = (event) => {
                        if (event.data.size > 0) {
                            audioChunksRef.current.push(event.data);
                        }
                    };

                    mediaRecorder.onstop = () => {
                        const blob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType || 'audio/webm' });
                        const url = URL.createObjectURL(blob);
                        setAudioBlob(blob);
                        setAudioUrl(url);
                        stream.getTracks().forEach(track => track.stop());
                        clearInterval(timerRef.current);
                    };

                    mediaRecorder.start(1000);
                    setIsRecording(true);
                    timerRef.current = setInterval(() => {
                        setRecordingDuration(prev => prev + 1);
                    }, 1000);
                    toast.info('Recording started... Speak now!');
                    return;
                } catch (retryError) {
                    toast.error('Could not start recording. Please try a different browser.');
                }
            } else {
                toast.error('Could not access microphone. Please check your browser settings and reload the page.');
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            clearInterval(timerRef.current);
        }
    };

    const deleteRecording = () => {
        if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
        }
        setAudioBlob(null);
        setAudioUrl(null);
        setRecordingDuration(0);
        setIsPlaying(false);
    };

    const togglePlayback = () => {
        if (audioRef.current) {
            if (isPlaying) {
                audioRef.current.pause();
            } else {
                audioRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Cleanup audio URL on unmount
    useEffect(() => {
        return () => {
            if (audioUrl) {
                URL.revokeObjectURL(audioUrl);
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [audioUrl]);
    useEffect(() => {
        if (step === 2) {
            fetchPrompts();
        }
    }, [step, moodRating, emoji]);

    const fetchPrompts = async () => {
        setLoadingPrompts(true);
        try {
            const response = await api.post('/ai/generate-prompts', {
                mood_rating: moodRating,
                emoji: emoji,
                song_or_genre: songOrGenre || null
            });
            setPrompts(response.data.prompts);
        } catch (error) {
            console.error('Failed to fetch prompts:', error);
        } finally {
            setLoadingPrompts(false);
        }
    };

    const getLocation = () => {
        setLoadingLocation(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    setLocation({ latitude, longitude });
                    
                    // Reverse geocode to get location name
                    try {
                        const response = await fetch(
                            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
                        );
                        const data = await response.json();
                        const city = data.address?.city || data.address?.town || data.address?.village || '';
                        const country = data.address?.country || '';
                        setLocationName(`${city}${city && country ? ', ' : ''}${country}`);
                    } catch {
                        setLocationName('Location saved');
                    }
                    setLoadingLocation(false);
                    toast.success('Location captured!');
                },
                (error) => {
                    console.error('Location error:', error);
                    setLoadingLocation(false);
                    toast.error('Could not get location. Please enable location services.');
                }
            );
        } else {
            setLoadingLocation(false);
            toast.error('Geolocation is not supported by your browser');
        }
    };

    const handleSubmit = async () => {
        // Validate based on entry type
        if (entryType === 'text' && !content.trim()) {
            toast.error('Please write something in your journal entry');
            return;
        }
        if (entryType === 'voice' && !audioBlob) {
            toast.error('Please record a voice entry');
            return;
        }

        setSaving(true);
        try {
            let audioData = null;
            if (entryType === 'voice' && audioBlob) {
                // Convert blob to base64
                const reader = new FileReader();
                audioData = await new Promise((resolve) => {
                    reader.onloadend = () => resolve(reader.result);
                    reader.readAsDataURL(audioBlob);
                });
            }

            const entryData = {
                entry_type: entryType,
                mood_rating: moodRating,
                emoji,
                song_or_genre: songOrGenre || null,
                content: entryType === 'text' ? content : '',
                audio_data: audioData,
                audio_duration: entryType === 'voice' ? recordingDuration : null,
                latitude: location?.latitude || null,
                longitude: location?.longitude || null,
                location_name: locationName || null,
                custom_colors: customColors.background || customColors.accent ? customColors : null,
                text_color: textColor || null,
                stickers: selectedStickers
            };

            await api.post('/journal/entries', entryData);
            toast.success('Entry saved successfully!');
            
            // Check for new achievements after saving
            try {
                if (user?.account_type === 'child') {
                    // Check child rewards for new achievements
                    const rewardsResponse = await api.get(`/family/child/${user.id}/rewards`);
                    const rewards = rewardsResponse.data;
                    
                    // Show notifications for newly unlocked badges
                    if (rewards.newly_unlocked_badges?.length > 0) {
                        rewards.newly_unlocked_badges.forEach(badge => {
                            toast.success(`Badge Unlocked: ${badge.name}!`, {
                                description: badge.description,
                                duration: 6000
                            });
                        });
                    }
                    
                    // Show notifications for newly unlocked themes
                    if (rewards.newly_unlocked_themes?.length > 0) {
                        rewards.newly_unlocked_themes.forEach(theme => {
                            toast.success(`Theme Unlocked: ${theme.name}!`, {
                                description: `Go to Rewards to try it out!`,
                                duration: 6000
                            });
                        });
                    }
                } else {
                    // Check adult rewards for new achievements
                    const rewardsResponse = await api.get('/journal/rewards');
                    const rewards = rewardsResponse.data;
                    
                    // Show notifications for newly unlocked badges
                    if (rewards.new_badges?.length > 0) {
                        rewards.new_badges.forEach(badge => {
                            toast.success(`Badge Unlocked: ${badge.name}!`, {
                                description: badge.description,
                                duration: 6000
                            });
                        });
                    }
                    
                    // Show notifications for newly unlocked themes
                    if (rewards.new_themes?.length > 0) {
                        rewards.new_themes.forEach(theme => {
                            toast.success(`Theme Unlocked: ${theme.name}!`, {
                                description: `Check your Rewards page to use it!`,
                                duration: 6000
                            });
                        });
                    }
                }
            } catch (rewardError) {
                // Silently fail - don't block the user from navigating
                console.log('Could not check rewards:', rewardError);
            }
            
            navigate('/dashboard');
        } catch (error) {
            console.error('Failed to save entry:', error);
            toast.error('Failed to save entry. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <DashboardLayout>
            <div data-testid="new-entry-page" className="max-w-4xl mx-auto">
                {/* Progress Steps */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    {[1, 2, 3].map((s) => (
                        <div
                            key={s}
                            className={`w-3 h-3 rounded-full transition-all duration-300 ${
                                s === step 
                                    ? 'w-8 bg-primary' 
                                    : s < step 
                                        ? 'bg-primary/50' 
                                        : 'bg-muted'
                            }`}
                        />
                    ))}
                </div>

                <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* Step 1: Mood Selection */}
                    {step === 1 && (
                        <Card className="glass-card">
                            <CardHeader>
                                <CardTitle className="font-heading text-2xl text-center">
                                    How are you feeling right now?
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-8 p-8">
                                {/* Mood Orb Preview */}
                                <div className="flex justify-center">
                                    <MoodOrb moodRating={moodRating} emoji={emoji} size="lg" />
                                </div>

                                {/* Mood Slider */}
                                <div className="space-y-4">
                                    <Label className="text-lg">Mood Rating: {moodRating}/10</Label>
                                    <Slider
                                        data-testid="mood-slider"
                                        value={[moodRating]}
                                        onValueChange={(value) => setMoodRating(value[0])}
                                        min={1}
                                        max={10}
                                        step={1}
                                        className="py-4"
                                    />
                                    <div className="flex justify-between text-sm text-muted-foreground">
                                        <span>Struggling</span>
                                        <span>Okay</span>
                                        <span>Great!</span>
                                    </div>
                                </div>

                                {/* Emoji Selection */}
                                <div className="space-y-4">
                                    <Label className="text-lg">Choose an emoji that matches your mood</Label>
                                    <div className="flex flex-wrap gap-3 justify-center">
                                        {EMOJI_OPTIONS.map((e) => (
                                            <button
                                                key={e}
                                                data-testid={`emoji-${e}`}
                                                onClick={() => setEmoji(e)}
                                                className={`text-3xl p-3 rounded-xl transition-all duration-300 ${
                                                    emoji === e 
                                                        ? 'bg-primary/20 scale-110 ring-2 ring-primary' 
                                                        : 'hover:bg-muted'
                                                }`}
                                            >
                                                {e}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Song/Genre Input */}
                                <div className="space-y-2">
                                    <Label className="text-lg flex items-center gap-2">
                                        <Music className="w-5 h-5" />
                                        What song or genre matches your mood?
                                    </Label>
                                    <Input
                                        data-testid="song-input"
                                        placeholder="e.g., Lo-fi beats, Adele - Someone Like You"
                                        value={songOrGenre}
                                        onChange={(e) => setSongOrGenre(e.target.value)}
                                        className="h-12 rounded-xl"
                                    />
                                </div>

                                {/* Location Button */}
                                <div className="flex items-center gap-4">
                                    <Button
                                        data-testid="location-btn"
                                        variant="outline"
                                        onClick={getLocation}
                                        disabled={loadingLocation}
                                        className="rounded-full"
                                    >
                                        {loadingLocation ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <MapPin className="w-4 h-4 mr-2" />
                                        )}
                                        {locationName || 'Add Location'}
                                    </Button>
                                    {locationName && (
                                        <span className="text-sm text-muted-foreground">
                                            📍 {locationName}
                                        </span>
                                    )}
                                </div>

                                <div className="flex justify-end">
                                    <Button
                                        data-testid="next-step-btn"
                                        onClick={() => setStep(2)}
                                        className="rounded-full px-8"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 2: Writing */}
                    {step === 2 && (
                        <Card className="glass-card">
                            <CardHeader>
                                <CardTitle className="font-heading text-2xl text-center">
                                    Express yourself freely
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6 p-8">
                                {/* Entry Type Selection */}
                                <div className="space-y-3">
                                    <Label className="text-lg">Choose your entry type</Label>
                                    <div className="flex gap-4">
                                        <Button
                                            variant={entryType === 'text' ? 'default' : 'outline'}
                                            onClick={() => setEntryType('text')}
                                            className="flex-1 rounded-xl h-16"
                                            data-testid="text-entry-btn"
                                        >
                                            <PenLine className="w-5 h-5 mr-2" />
                                            Text Entry
                                        </Button>
                                        <Button
                                            variant={entryType === 'voice' ? 'default' : 'outline'}
                                            onClick={() => setEntryType('voice')}
                                            className="flex-1 rounded-xl h-16"
                                            data-testid="voice-entry-btn"
                                        >
                                            <Mic className="w-5 h-5 mr-2" />
                                            Voice Entry
                                        </Button>
                                    </div>
                                </div>

                                {/* AI Prompts - Only show for text entries */}
                                {entryType === 'text' && (
                                    <div className="space-y-3">
                                        <Label className="text-lg flex items-center gap-2">
                                            <Sparkles className="w-5 h-5 text-chart-3" />
                                            Writing Prompts for You
                                    </Label>
                                    {loadingPrompts ? (
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Generating personalized prompts...
                                        </div>
                                    ) : (
                                        <div className="space-y-2">
                                            {prompts.map((prompt, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setContent(prompt.replace(/^\d+\.\s*/, '') + '\n\n')}
                                                    className="block w-full text-left p-3 rounded-xl bg-accent/30 hover:bg-accent/50 transition-colors text-sm"
                                                >
                                                    {prompt}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                )}

                                {/* Text Entry */}
                                {entryType === 'text' && (
                                    <div className="space-y-2">
                                        <Label className="text-lg">Your Journal Entry</Label>
                                        <Textarea
                                            data-testid="journal-content"
                                            placeholder="Write freely about how you're feeling, what's on your mind, or anything you want to express..."
                                            value={content}
                                            onChange={(e) => setContent(e.target.value)}
                                            className="min-h-[300px] rounded-xl resize-none text-lg leading-relaxed"
                                            style={{ 
                                                color: textColor || undefined,
                                                fontFamily: fontFamily || undefined
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Voice Entry */}
                                {entryType === 'voice' && (
                                    <div className="space-y-4">
                                        <Label className="text-lg">Record Your Voice Entry</Label>
                                        
                                        {/* Not supported message */}
                                        {!recordingSupported && (
                                            <div className="p-4 rounded-xl bg-destructive/10 border border-destructive/20 text-center">
                                                <p className="text-destructive font-medium">Voice recording is not supported on this browser.</p>
                                                <p className="text-sm text-muted-foreground mt-2">
                                                    Please try using Chrome, Firefox, or Safari 14.3+ on a supported device.
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Recording Controls */}
                                        {recordingSupported && (
                                        <div className="flex flex-col items-center gap-6 p-8 rounded-2xl bg-muted/30">
                                            {!audioBlob ? (
                                                <>
                                                    {/* Recording Button */}
                                                    <button
                                                        onClick={isRecording ? stopRecording : startRecording}
                                                        className={`w-24 h-24 rounded-full flex items-center justify-center transition-all ${
                                                            isRecording 
                                                                ? 'bg-destructive hover:bg-destructive/90 animate-pulse' 
                                                                : 'bg-primary hover:bg-primary/90'
                                                        }`}
                                                        data-testid={isRecording ? "stop-recording-btn" : "start-recording-btn"}
                                                    >
                                                        {isRecording ? (
                                                            <Square className="w-10 h-10 text-white" />
                                                        ) : (
                                                            <Mic className="w-10 h-10 text-white" />
                                                        )}
                                                    </button>
                                                    
                                                    {/* Recording Status */}
                                                    {isRecording ? (
                                                        <div className="flex flex-col items-center gap-2">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-3 h-3 bg-destructive rounded-full animate-pulse" />
                                                                <span className="text-lg font-medium">{formatDuration(recordingDuration)}</span>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground">Recording... Tap to stop</p>
                                                        </div>
                                                    ) : (
                                                        <>
                                                        <p className="text-muted-foreground">Tap to start recording</p>
                                                        <p className="text-xs text-muted-foreground text-center max-w-xs">
                                                            💡 On iPhone: Make sure to allow microphone access when prompted. 
                                                            You may need to enable it in Settings → Safari → Microphone.
                                                        </p>
                                                        </>
                                                    )}
                                                </>
                                            ) : (
                                                <>
                                                    {/* Audio Player */}
                                                    <audio
                                                        ref={audioRef}
                                                        src={audioUrl}
                                                        onEnded={() => setIsPlaying(false)}
                                                        className="hidden"
                                                    />
                                                    
                                                    <div className="flex items-center gap-4">
                                                        <button
                                                            onClick={togglePlayback}
                                                            className="w-16 h-16 rounded-full bg-primary flex items-center justify-center hover:bg-primary/90"
                                                            data-testid="play-audio-btn"
                                                        >
                                                            {isPlaying ? (
                                                                <Pause className="w-8 h-8 text-white" />
                                                            ) : (
                                                                <Play className="w-8 h-8 text-white ml-1" />
                                                            )}
                                                        </button>
                                                        
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">Voice Entry Recorded</span>
                                                            <span className="text-sm text-muted-foreground">
                                                                Duration: {formatDuration(recordingDuration)}
                                                            </span>
                                                        </div>
                                                        
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={deleteRecording}
                                                            className="text-destructive hover:text-destructive/90"
                                                            data-testid="delete-recording-btn"
                                                        >
                                                            <Trash2 className="w-5 h-5" />
                                                        </Button>
                                                    </div>
                                                    
                                                    <p className="text-sm text-muted-foreground text-center">
                                                        Your voice entry will be saved with your journal. You can play it back anytime.
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                        )}
                                    </div>
                                )}

                                {/* Music Player */}
                                <MusicPlayer />

                                <div className="flex justify-between">
                                    <Button
                                        variant="outline"
                                        onClick={() => setStep(1)}
                                        className="rounded-full"
                                    >
                                        <ChevronLeft className="w-4 h-4 mr-2" />
                                        Back
                                    </Button>
                                    <Button
                                        data-testid="next-step-2-btn"
                                        onClick={() => setStep(3)}
                                        className="rounded-full px-8"
                                    >
                                        Next
                                        <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Step 3: Customize & Save */}
                    {step === 3 && (
                        <Card className="glass-card">
                            <CardHeader>
                                <CardTitle className="font-heading text-2xl text-center">
                                    Customize Your Entry
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6 p-8">
                                {/* Preview */}
                                <div 
                                    className="p-6 rounded-2xl border border-border relative"
                                    style={{ 
                                        backgroundColor: customColors.background || undefined,
                                        color: textColor || undefined,
                                        fontFamily: fontFamily || undefined
                                    }}
                                >
                                    {/* Stickers Display */}
                                    {selectedStickers.length > 0 && (
                                        <div className="absolute top-2 right-2 flex gap-1">
                                            {selectedStickers.map(stickerId => {
                                                const sticker = STICKER_OPTIONS.find(s => s.id === stickerId);
                                                return sticker ? (
                                                    <span key={stickerId} className="text-xl">{sticker.emoji}</span>
                                                ) : null;
                                            })}
                                        </div>
                                    )}
                                    
                                    <div className="flex items-center gap-3 mb-4">
                                        <span className="text-3xl">{emoji}</span>
                                        <span className="text-xl font-medium">Mood: {moodRating}/10</span>
                                    </div>
                                    <p className="whitespace-pre-wrap">
                                        {content.substring(0, 200)}
                                        {content.length > 200 && '...'}
                                    </p>
                                    {songOrGenre && (
                                        <p className="mt-4 text-sm opacity-70">
                                            🎵 {songOrGenre}
                                        </p>
                                    )}
                                    {locationName && (
                                        <p className="mt-2 text-sm opacity-70">
                                            📍 {locationName}
                                        </p>
                                    )}
                                </div>

                                {/* Sticker Selection */}
                                <div className="space-y-4">
                                    <Label className="text-lg flex items-center gap-2">
                                        <Sticker className="w-5 h-5" />
                                        Add Stickers
                                    </Label>
                                    <div className="flex flex-wrap gap-2">
                                        {STICKER_OPTIONS.map((sticker) => (
                                            <button
                                                key={sticker.id}
                                                onClick={() => toggleSticker(sticker.id)}
                                                className={`text-2xl p-2 rounded-xl transition-all ${
                                                    selectedStickers.includes(sticker.id)
                                                        ? 'bg-primary/20 ring-2 ring-primary scale-110'
                                                        : 'bg-muted/30 hover:bg-muted/50'
                                                }`}
                                                title={sticker.name}
                                                data-testid={`sticker-${sticker.id}`}
                                            >
                                                {sticker.emoji}
                                            </button>
                                        ))}
                                    </div>
                                    {selectedStickers.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setSelectedStickers([])}
                                            className="text-muted-foreground"
                                        >
                                            <X className="w-4 h-4 mr-1" />
                                            Clear stickers
                                        </Button>
                                    )}
                                </div>

                                {/* Color Customization */}
                                <div className="space-y-4">
                                    <Label className="text-lg flex items-center gap-2">
                                        <Palette className="w-5 h-5" />
                                        Color Theme
                                    </Label>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-sm">Background Color</Label>
                                            <Input
                                                type="color"
                                                value={customColors.background || '#ffffff'}
                                                onChange={(e) => setCustomColors({
                                                    ...customColors,
                                                    background: e.target.value
                                                })}
                                                className="h-12 rounded-xl cursor-pointer"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-sm">Text Color</Label>
                                            <Input
                                                type="color"
                                                value={textColor || '#000000'}
                                                onChange={(e) => setTextColor(e.target.value)}
                                                className="h-12 rounded-xl cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                    
                                    {(customColors.background || textColor) && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setCustomColors({ background: '', accent: '' });
                                                setTextColor('');
                                            }}
                                            className="text-muted-foreground"
                                        >
                                            <X className="w-4 h-4 mr-1" />
                                            Reset colors
                                        </Button>
                                    )}
                                </div>

                                <div className="flex justify-between pt-4">
                                    <Button
                                        variant="outline"
                                        onClick={() => setStep(2)}
                                        className="rounded-full"
                                    >
                                        <ChevronLeft className="w-4 h-4 mr-2" />
                                        Back
                                    </Button>
                                    <Button
                                        data-testid="save-entry-btn"
                                        onClick={handleSubmit}
                                        disabled={saving || (entryType === 'text' && !content.trim()) || (entryType === 'voice' && !audioBlob)}
                                        className="rounded-full px-8 shadow-lg"
                                    >
                                        {saving ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4 mr-2" />
                                        )}
                                        Save Entry
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </motion.div>
            </div>
        </DashboardLayout>
    );
};

export default NewEntryPage;
